import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SchoolLayout } from "./SchoolLayout";
import { Card, CardContent } from "@/project/karuna/components/ui/card"; // @ts-ignore - card is not a valid module
import { Button } from "@/project/karuna/components/ui/button"; // @ts-ignore - button is not a valid module
import { Input } from "@/project/karuna/components/ui/input"; // @ts-ignore - input is not a valid module
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/project/karuna/components/ui/tabs"; // @ts-ignore - tabs is not a valid module
import { Search, Calendar, MapPin, Eye } from "lucide-react";
import { useKarunaLink } from "@/project/karuna/utils/navigationRoutes";
import { DynamicModuleService, UploadService } from "@/services/APIs";
import { useQuery } from "@tanstack/react-query";
import {
  KarunaPagination,
  ListCardViewToggle,
  type KarunaViewMode,
} from "@/project/karuna/components/shared/KarunaListControls";
import { useKarunaPagedList } from "@/project/karuna/hooks/useKarunaPagedList";
import { SchoolEventSubmissionDialog } from "@/project/karuna/pages/school/SchoolEventSubmissionForm";
import {
  mapWithConcurrency,
  DEFAULT_API_FETCH_CONCURRENCY,
} from "@/project/karuna/utils/mapWithConcurrency";

const SCHEMA_NAME = "Activity Management";

interface Activity {
  id?: string;
  _id?: string;
  title?: string;
  activitytitle?: string;
  activity_title?: string;
  description?: string;
  startdate?: string;
  start_date?: string;
  enddate?: string;
  end_date?: string;
  location?: string;
  banner?: any;
  banner_url?: string | string[];
  thumbnail_url?: string | string[];
  status?: string;
  [key: string]: any;
}

type ActivityStatus = "ongoing" | "upcoming" | "completed" | "all";

const SchoolActivities = () => {
  const navigate = useNavigate();
  const getLink = useKarunaLink();
  const [searchParams, setSearchParams] = useSearchParams();
  const [submitModalId, setSubmitModalId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState<ActivityStatus>("all");
  const [thumbnailImages, setThumbnailImages] = useState<
    Record<string, string>
  >({});
  const [fullActivityDataMap, setFullActivityDataMap] = useState<
    Record<string, any>
  >({});
  const [viewMode, setViewMode] = useState<KarunaViewMode>("card");

  // Listen for submission events to refresh
  useEffect(() => {
    const handleSubmissionCreated = () => {
      // refetchSubmissions();
    };
    window.addEventListener("submission-created", handleSubmissionCreated);
    return () => {
      window.removeEventListener("submission-created", handleSubmissionCreated);
    };
  }, []);

  // Deep link: /school/activities?submit=<activityId> (e.g. from legacy /school/submit/:id)
  useEffect(() => {
    const fromQuery = searchParams.get("submit");
    if (!fromQuery) {
      return;
    }
    setSubmitModalId(fromQuery);
    setSearchParams(
      params => {
        const next = new URLSearchParams(params);
        next.delete("submit");
        return next;
      },
      { replace: true }
    );
  }, [searchParams, setSearchParams]);

  // Fetch activities data with full details
  const { data: activitiesData, isLoading } = useQuery({
    queryKey: ["activities", SCHEMA_NAME],
    queryFn: async () => {
      try {
        const activitiesList =
          await DynamicModuleService.getGridColItemsAllPages(SCHEMA_NAME);

        const activitiesWithFullData = await mapWithConcurrency(
          activitiesList,
          DEFAULT_API_FETCH_CONCURRENCY,
          async (activity: any) => {
            const activityId = activity.id ?? activity._id;
            if (!activityId) {
              return activity;
            }

            try {
              const fullData = await DynamicModuleService.getItemById(
                SCHEMA_NAME,
                activityId
              );
              const data = fullData?.data ?? fullData ?? activity;
              return { ...activity, ...data };
            } catch (err) {
              console.warn(
                `Could not fetch full data for activity ${activityId}:`,
                err
              );
              return activity;
            }
          }
        );

        return activitiesWithFullData;
      } catch (error) {
        console.error("Error fetching activities:", error);
        return [];
      }
    },
  });

  // Helper function to extract image URL from various formats
  const extractImageUrl = useCallback(
    async (imageValue: any): Promise<string | null> => {
      if (!imageValue) {
        return null;
      }

      // If it's already a URL string
      if (typeof imageValue === "string") {
        if (
          imageValue.startsWith("http://") ||
          imageValue.startsWith("https://")
        ) {
          return imageValue;
        }

        // Check if it's a JSON string (starts with [ or {)
        if (
          imageValue.trim().startsWith("[") ||
          imageValue.trim().startsWith("{")
        ) {
          try {
            const parsed = JSON.parse(imageValue);
            // Recursively resolve the parsed value
            return await extractImageUrl(parsed);
          } catch (e) {
            // If parsing fails, it's not valid JSON, continue as regular string
            console.warn("Failed to parse JSON string:", e);
          }
        }

        // If it's a file ID, fetch the image URL
        try {
          const fileResponse = await UploadService.getFileById({
            id: imageValue,
          });
          return (
            fileResponse?.file_path_thumbnail ??
            fileResponse?.file_path ??
            fileResponse?.url ??
            null
          );
        } catch (error) {
          console.warn(`Failed to fetch image for ID ${imageValue}:`, error);
          return null;
        }
      }

      // If it's an array
      if (Array.isArray(imageValue) && imageValue.length > 0) {
        const firstItem = imageValue[0];

        if (typeof firstItem === "string") {
          return extractImageUrl(firstItem);
        }

        if (typeof firstItem === "object" && firstItem !== null) {
          if (firstItem.file_path_thumbnail) {
            return firstItem.file_path_thumbnail;
          }
          if (firstItem.file_path) {
            return firstItem.file_path;
          }
          if (firstItem.url) {
            return typeof firstItem.url === "string"
              ? firstItem.url
              : await extractImageUrl(firstItem.url);
          }
          if (firstItem.id) {
            return await extractImageUrl(firstItem.id);
          }
          if (firstItem.file_id) {
            return await extractImageUrl(firstItem.file_id);
          }
        }
      }

      // If it's an object
      if (
        typeof imageValue === "object" &&
        imageValue !== null &&
        !Array.isArray(imageValue)
      ) {
        if (imageValue.file_path_thumbnail) {
          return imageValue.file_path_thumbnail;
        }
        if (imageValue.file_path) {
          return imageValue.file_path;
        }
        if (imageValue.url) {
          return typeof imageValue.url === "string"
            ? imageValue.url
            : await extractImageUrl(imageValue.url);
        }
        if (imageValue.id) {
          return await extractImageUrl(imageValue.id);
        }
      }

      return null;
    },
    []
  );

  // Fetch banner images and full activity data for activities
  useEffect(() => {
    const fetchBannerImages = async () => {
      if (!activitiesData || !Array.isArray(activitiesData)) {
        return;
      }

      const imageMap: Record<string, string> = {};
      const fullDataMap: Record<string, any> = {};

      const results = await mapWithConcurrency(
        activitiesData,
        DEFAULT_API_FETCH_CONCURRENCY,
        async activity => {
          const activityId = activity.id ?? activity._id;
          if (!activityId) {
            return null;
          }

          try {
            let imageUrl: string | null = null;
            let bannerValue = activity.banner;
            let fullActivityData = activity;

            // Check if we need to fetch full activity data (missing banner/thumbnail or description)
            const needsFullData =
              !bannerValue || !activity.thumbnail || !activity.description;

            if (needsFullData) {
              try {
                const fullActivity = await DynamicModuleService.getItemById(
                  SCHEMA_NAME,
                  activityId
                );

                if (fullActivity) {
                  fullActivityData = fullActivity;
                  fullDataMap[activityId] = fullActivity;
                  bannerValue =
                    fullActivity.banner ??
                    fullActivity.banner_image ??
                    fullActivity.banner_url;
                }
              } catch (fetchError) {
                console.warn(
                  `⚠️ Failed to fetch full activity for ${activityId}:`,
                  fetchError
                );
              }
            }

            // First, prioritize the "thumbnail" field for the list view
            let thumbnailValue =
              fullActivityData.thumbnail ??
              activity.thumbnail ??
              fullActivityData.thumbnail_url ??
              activity.thumbnail_url ??
              fullActivityData.thumbnail_image ??
              activity.thumbnail_image;

            // Handle case where thumbnail might be a JSON string
            if (
              typeof thumbnailValue === "string" &&
              thumbnailValue.trim().startsWith("[")
            ) {
              try {
                thumbnailValue = JSON.parse(thumbnailValue);
              } catch (e) {
                console.warn(
                  `Failed to parse thumbnail JSON for ${activityId}:`,
                  e
                );
              }
            }

            if (thumbnailValue) {
              imageUrl = await extractImageUrl(thumbnailValue);
              if (imageUrl) {
                return { activityId, imageUrl };
              }
            }

            // Handle case where banner might be a JSON string
            if (
              typeof bannerValue === "string" &&
              bannerValue.trim().startsWith("[")
            ) {
              try {
                bannerValue = JSON.parse(bannerValue);
              } catch (e) {
                console.warn(
                  `Failed to parse banner JSON for ${activityId}:`,
                  e
                );
              }
            }

            // Fallback to banner if thumbnail not found
            if (bannerValue) {
              imageUrl = await extractImageUrl(bannerValue);
              if (imageUrl) {
                return { activityId, imageUrl };
              }
            }

            // If banner field not found or failed, check other possible fields as fallback
            const possibleImageFields = [
              "banner_image",
              "banner_url",
              "image",
              "photo",
              "picture",
            ];

            for (const fieldName of possibleImageFields) {
              const fieldValue =
                fullActivityData[fieldName] ?? activity[fieldName];
              if (fieldValue) {
                imageUrl = await extractImageUrl(fieldValue);
                if (imageUrl) {
                  return { activityId, imageUrl };
                }
              }
            }

            return null;
          } catch (error) {
            console.error(
              `Error processing banner for activity ${activityId}:`,
              error
            );
            return null;
          }
        }
      );

      results.forEach(result => {
        if (result) {
          imageMap[result.activityId] = result.imageUrl;
        }
      });

      setThumbnailImages(imageMap);
      setFullActivityDataMap(fullDataMap);
    };

    fetchBannerImages();
  }, [activitiesData, extractImageUrl]);

  // Calculate activity status based on dates (aligned with Admin + School Dashboard)
  // Upcoming = today is before start; ongoing = within [start, end]; completed = after end
  const getActivityStatus = (activity: Activity): ActivityStatus => {
    const startDateStr = activity.startdate ?? activity.start_date;
    const endDateStr = activity.enddate ?? activity.end_date;

    if (!startDateStr || !endDateStr) {
      return "upcoming";
    }

    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const startDate = new Date(startDateStr);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(endDateStr);
    endDate.setHours(0, 0, 0, 0);

    if (now < startDate) {
      return "upcoming";
    }
    if (now >= startDate && now <= endDate) {
      return "ongoing";
    }
    return "completed";
  };

  // Process and normalize activities
  const activities: Activity[] = useMemo(() => {
    if (!activitiesData || !Array.isArray(activitiesData)) {
      return [];
    }

    const processedActivities = activitiesData.map((activity: any) => {
      const activityId = activity.id ?? activity._id;
      // Merge full activity data if available
      const fullData = fullActivityDataMap[activityId] ?? {};
      const mergedActivity = { ...activity, ...fullData };

      return {
        ...mergedActivity,
        title:
          mergedActivity.activitytitle ??
          mergedActivity.title ??
          mergedActivity.activity_title ??
          "Untitled Activity",
        status: mergedActivity.status ?? getActivityStatus(mergedActivity),
      };
    });

    // Sort by orderby field (ascending), fallback to a high number if not present
    return processedActivities.sort((a, b) => {
      const orderA =
        a.orderby !== undefined &&
        a.orderby !== null &&
        !isNaN(Number(a.orderby))
          ? Number(a.orderby)
          : 999999;
      const orderB =
        b.orderby !== undefined &&
        b.orderby !== null &&
        !isNaN(Number(b.orderby))
          ? Number(b.orderby)
          : 999999;
      return orderA - orderB;
    });
  }, [activitiesData, fullActivityDataMap]);

  // Filter activities based on search and tab
  const filteredActivities = useMemo(() => {
    let filtered = activities;

    // Filter by tab
    if (activeTab !== "all") {
      filtered = filtered.filter(
        activity => getActivityStatus(activity) === activeTab
      );
    }

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(activity => {
        const titleMatch =
          activity.title?.toLowerCase().includes(searchLower) ?? false;
        const activityTitleMatch =
          activity.activitytitle?.toLowerCase().includes(searchLower) ?? false;
        const descriptionMatch =
          activity.description?.toLowerCase().includes(searchLower) ?? false;
        const locationMatch =
          activity.location?.toLowerCase().includes(searchLower) ?? false;
        return (
          titleMatch || activityTitleMatch || descriptionMatch || locationMatch
        );
      });
    }

    return filtered;
  }, [activities, activeTab, searchTerm]);

  const {
    page: schoolActPage,
    setPage: setSchoolActPage,
    totalPages: schoolActTotalPages,
    paginatedItems: pagedSchoolActivities,
    totalItems: schoolActFilteredTotal,
    pageSize: schoolActPageSize,
    setPageSize: setSchoolActPageSize,
  } = useKarunaPagedList(filteredActivities, searchTerm, activeTab);

  // Calculate statistics
  const statistics = useMemo(() => {
    const total = activities.length;
    const ongoing = activities.filter(
      a => getActivityStatus(a) === "ongoing"
    ).length;
    const upcoming = activities.filter(
      a => getActivityStatus(a) === "upcoming"
    ).length;
    const completed = activities.filter(
      a => getActivityStatus(a) === "completed"
    ).length;

    return { total, ongoing, upcoming, completed };
  }, [activities]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const handleViewDetails = (activity: Activity) => {
    const activityId = activity.id ?? activity._id;
    if (activityId) {
      navigate(getLink(`/school/activities/${activityId}`));
    }
  };

  const handleSubmit = (activity: Activity) => {
    const activityId = activity.id ?? activity._id;
    if (activityId) {
      setSubmitModalId(String(activityId));
    }
  };

  return (
    <SchoolLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-3xl font-bold">
            Available Activities
          </h1>
          <p className="mt-1 text-muted-foreground">
            Browse and participate in Karuna activities
          </p>
        </div>

        {/* Statistics Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardContent className="p-6">
              <div className="text-3xl font-bold text-blue-600">
                {statistics.total}
              </div>
              <div className="text-sm text-muted-foreground">
                Total Activities
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="text-3xl font-bold text-green-600">
                {statistics.ongoing}
              </div>
              <div className="text-sm text-muted-foreground">Ongoing</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="text-3xl font-bold text-orange-600">
                {statistics.upcoming}
              </div>
              <div className="text-sm text-muted-foreground">Upcoming</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="text-3xl font-bold text-purple-600">
                {statistics.completed}
              </div>
              <div className="text-sm text-muted-foreground">Completed</div>
            </CardContent>
          </Card>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search activities by name or category..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Tabs and Activity Cards */}
        <Tabs
          value={activeTab}
          onValueChange={v => setActiveTab(v as ActivityStatus)}
        >
          <TabsList>
            <TabsTrigger value="all">All Activities</TabsTrigger>
            <TabsTrigger value="ongoing">Ongoing</TabsTrigger>
            <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
            <TabsTrigger value="completed">Completed</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-6">
            {isLoading ? (
              <div className="py-12 text-center">
                <p className="text-muted-foreground">Loading activities...</p>
              </div>
            ) : filteredActivities.length === 0 ? (
              <div className="py-12 text-center">
                <p className="text-muted-foreground">No activities found</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <ListCardViewToggle
                    viewMode={viewMode}
                    onViewChange={setViewMode}
                  />
                </div>

                {viewMode === "card" ? (
                  <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {pagedSchoolActivities.map(activity => {
                      const activityId = String(
                        activity.id ?? activity._id ?? ""
                      ).trim();
                      const status = getActivityStatus(activity);
                      const thumbnailImage = thumbnailImages[activityId];
                      const startDateStr =
                        activity.startdate ?? activity.start_date;

                      // Submissions allowed unlimited times - always show Submit for ongoing activities

                      return (
                        <Card
                          key={activityId}
                          className="group overflow-hidden transition-shadow hover:shadow-lg"
                        >
                          {/* Thumbnail Image */}
                          {thumbnailImage ? (
                            <div className="aspect-video w-full overflow-hidden bg-muted">
                              <img
                                src={thumbnailImage}
                                alt={activity.title}
                                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                                onError={e => {
                                  console.error(
                                    `❌ Image failed to load for ${activityId}:`,
                                    thumbnailImage
                                  );
                                  e.currentTarget.style.display = "none";
                                }}
                              />
                            </div>
                          ) : (
                            <div className="aspect-video w-full bg-gradient-to-br from-primary/20 to-primary/5" />
                          )}

                          <CardContent className="p-5">
                            {/* Title and Status */}
                            <div className="mb-2 flex items-start justify-between gap-2">
                              <h3 className="line-clamp-1 flex-1 text-lg font-semibold">
                                {activity.title}
                              </h3>
                            </div>

                            {/* Description */}
                            {activity.description && (
                              <p className="mb-4 line-clamp-2 text-sm text-muted-foreground">
                                {activity.description}
                              </p>
                            )}

                            {/* Details */}
                            <div className="mb-4 space-y-2">
                              {startDateStr && (
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                  <Calendar className="h-4 w-4 shrink-0" />
                                  <span>{formatDate(startDateStr)}</span>
                                </div>
                              )}
                              {activity.location && (
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                  <MapPin className="h-4 w-4 shrink-0" />
                                  <span className="line-clamp-1">
                                    {activity.location}
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* Action Buttons */}
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                className="flex-1 gap-2"
                                onClick={() => handleViewDetails(activity)}
                              >
                                <Eye className="h-4 w-4" />
                                View
                              </Button>
                              {status === "upcoming" ||
                              status === "completed" ? (
                                <Button
                                  className="flex-1 cursor-not-allowed text-white opacity-50"
                                  style={{ backgroundColor: "#404bb5" }}
                                  disabled
                                >
                                  + Submit
                                </Button>
                              ) : (
                                <Button
                                  className="flex-1 text-white hover:opacity-90"
                                  style={{ backgroundColor: "#404bb5" }}
                                  onClick={() => handleSubmit(activity)}
                                >
                                  + Submit
                                </Button>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pagedSchoolActivities.map(activity => {
                      const activityId = String(
                        activity.id ?? activity._id ?? ""
                      ).trim();
                      const status = getActivityStatus(activity);
                      const thumbnailImage = thumbnailImages[activityId];
                      return (
                        <Card key={activityId}>
                          <CardContent className="p-4">
                            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                              <div className="relative h-20 w-full shrink-0 overflow-hidden rounded-md bg-muted sm:h-16 sm:w-28">
                                {thumbnailImage ? (
                                  <img
                                    src={thumbnailImage}
                                    alt=""
                                    className="h-full w-full object-cover"
                                  />
                                ) : null}
                              </div>
                              <div className="min-w-0 flex-1">
                                <h3 className="font-semibold">
                                  {activity.title}
                                </h3>
                                <p className="line-clamp-2 text-sm text-muted-foreground">
                                  {activity.description ?? ""}
                                </p>
                              </div>
                              <div className="flex shrink-0 flex-wrap gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="gap-2"
                                  onClick={() => handleViewDetails(activity)}
                                >
                                  <Eye className="h-4 w-4" />
                                  View
                                </Button>
                                {status === "upcoming" ||
                                status === "completed" ? (
                                  <Button
                                    className="cursor-not-allowed text-white opacity-50"
                                    style={{ backgroundColor: "#404bb5" }}
                                    disabled
                                    size="sm"
                                  >
                                    + Submit
                                  </Button>
                                ) : (
                                  <Button
                                    className="text-white hover:opacity-90"
                                    style={{ backgroundColor: "#404bb5" }}
                                    onClick={() => handleSubmit(activity)}
                                    size="sm"
                                  >
                                    + Submit
                                  </Button>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}

                <KarunaPagination
                  page={schoolActPage}
                  totalPages={schoolActTotalPages}
                  totalItems={schoolActFilteredTotal}
                  pageSize={schoolActPageSize}
                  onPageChange={setSchoolActPage}
                  onPageSizeChange={setSchoolActPageSize}
                />
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <SchoolEventSubmissionDialog
        open={!!submitModalId}
        onOpenChange={open => {
          if (!open) {
            setSubmitModalId(null);
          }
        }}
        eventId={submitModalId ?? undefined}
      />
    </SchoolLayout>
  );
};

export default SchoolActivities;
